import type { ComplianceCheck, CrosscheckCounts } from '@privisy/api-types';
import type { CcpaSectionName, ScanResult } from '../../utils/types';

// One classifier for the owner report and the shared summary (#1658 review).
export { countCrosscheck } from '@privisy/api-types';

/** The sections every CCPA count reads; `cipa` is deliberately absent (#1392). */
export const CCPA_SECTIONS: CcpaSectionName[] = ['uiCompliance', 'trackerDetection', 'gpcValidation', 'privacyPolicy'];

/**
 * The report's one CCPA check set: the four CCPA sections' checks, each tagged
 * with its section, then any non-`offSite` page check whose name no section
 * check already carries. ReportHero's chips and verdict, the "What to fix"
 * list, the passing list and the AI Crosscheck banner (#1658) all read it, so
 * their counts can't drift apart (#1899).
 *
 * Every crawled sub-page re-runs the UI stage under fresh ids, so a 25-page
 * monitored scan repeats one missing link 25 times. The backend scores that
 * finding once on the section (sub-page repeats become unscored insights), so
 * a page check collapses by name onto the section check, which wins. A page
 * check with no section counterpart is still kept, once per name.
 */
export function collectCcpaChecks(result: ScanResult): ComplianceCheck[] {
  // The four CCPA sections only: CIPA exposure signals (#1392) are another
  // statute's and must not move these CCPA counts.
  const sectionChecks = CCPA_SECTIONS.flatMap((k): ComplianceCheck[] =>
    (result.sections?.[k]?.checks || []).map((c) => ({ ...c, section: k })),
  );
  // An off-site page (#1364) describes a different business, so its checks
  // stay out of this site's totals.
  const pageChecks = result.pageResults?.filter((p) => !p.offSite).flatMap((p) => p.checks) ?? [];
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();
  return sectionChecks.concat(pageChecks).filter((c) => {
    if (seenIds.has(c.id) || seenNames.has(c.name)) return false;
    seenIds.add(c.id);
    seenNames.add(c.name);
    return true;
  });
}

/**
 * Which footer the explainer shows, from the persisted adjudication mode,
 * never POLICY_JUDGE_MODE: the frontend can't see the backend's env, and an
 * old report keeps the mode it was scanned under. `enforce` with nothing
 * corrected is its own case: the crosscheck could have changed a status.
 */
export function crosscheckMode(counts: CrosscheckCounts): 'advisory' | 'enforce' | 'corrected' {
  if (counts.corrected > 0) return 'corrected';
  return counts.mode === 'enforce' ? 'enforce' : 'advisory';
}
